import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchTimeline, SCHEDULER_KEYS } from "../api/schedulerApi";
import type { TimelineEntry } from "../api/schedulerApi";
import "./scheduler.css";

/* ── Constants ────────────────────────────────────────────────────── */

const TYPE_ICONS: Record<string, string> = {
  event:      "\uD83D\uDCC5",
  marker:     "\uD83D\uDCCC",
  deployment: "\uD83D\uDE80",
  incident:   "\u26A0\uFE0F",
  shift:      "\uD83D\uDD04",
  milestone:  "\uD83C\uDFC1",
  custom:     "\uD83D\uDCCC",
};

/* ── Component ────────────────────────────────────────────────────── */

export default function SchedulerTimeline() {
  // Fetch last 30 days by default
  const from = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }, []);
  const to = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const { data, isLoading } = useQuery({
    queryKey: SCHEDULER_KEYS.timeline(from, to),
    queryFn: () => fetchTimeline(from, to, 200),
    staleTime: 15_000,
  });

  const entries = data?.items ?? [];

  // Group by date
  const grouped = useMemo(() => {
    const map = new Map<string, TimelineEntry[]>();
    for (const entry of entries) {
      const dateKey = entry.date || entry.timestamp?.slice(0, 10) || "unknown";
      if (!map.has(dateKey)) map.set(dateKey, []);
      map.get(dateKey)!.push(entry);
    }
    // Sort dates descending
    return Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [entries]);

  if (isLoading) {
    return (
      <div className="sch-placeholder">
        <span className="sch-loading-dot" />
        <span>Loading timeline...</span>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="sch-placeholder">
        <span className="sch-placeholder__icon">&#x23F1;</span>
        <span>No timeline entries in the last 30 days</span>
      </div>
    );
  }

  return (
    <div className="tl-root">
      <div className="tl-header">
        <span className="tl-header__count">
          {data?.total ?? 0} entries ({from} to {to})
        </span>
      </div>

      <div className="tl-list">
        {grouped.map(([dateKey, items]) => (
          <div key={dateKey} className="tl-day-group">
            <div className="tl-day-label">{formatDateLabel(dateKey)}</div>
            <div className="tl-day-items">
              {items.map((entry: TimelineEntry) => (
                <div key={entry.id} className="tl-entry">
                  <div className="tl-entry__line" style={{ borderColor: entry.color }} />
                  <div className="tl-entry__dot" style={{ background: entry.color }} />
                  <div className="tl-entry__content">
                    <div className="tl-entry__header">
                      <span className="tl-entry__icon">
                        {entry.type === "marker"
                          ? TYPE_ICONS[entry.detail] || TYPE_ICONS.marker
                          : TYPE_ICONS.event}
                      </span>
                      <span className="tl-entry__label">{entry.label}</span>
                      <span className="tl-entry__type-badge" style={{ borderColor: entry.color }}>
                        {entry.type === "marker" ? entry.detail : "event"}
                      </span>
                    </div>
                    <div className="tl-entry__meta">
                      <span className="tl-entry__time">
                        {formatTime(entry.timestamp)}
                      </span>
                      {entry.detail && entry.type === "event" && (
                        <span className="tl-entry__detail">{entry.detail}</span>
                      )}
                      <span className="tl-entry__source">{entry.source}</span>
                      {entry.created_by && (
                        <span className="tl-entry__author">{entry.created_by}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Helpers ──────────────────────────────────────────────────────── */

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = Math.round((today.getTime() - d.getTime()) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";

  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatTime(ts: string): string {
  if (!ts) return "";
  const match = ts.match(/T(\d{2}:\d{2})/);
  return match ? match[1] : "";
}
